/**
 * t374u: one brand theme from the two colours an admin picks (docs/branding.md).
 *
 * - **Primary** colours the chrome: the navigation rail, the sign-in backdrop, dark ("ink") buttons. It has to carry
 *   light text, so a colour that's too light is darkened until it does.
 * - **Accent** is a fill (primary buttons, badges, toggles, the active nav bar, highlights). The text on it is black or
 *   white, whichever reads (yellow takes black). Accent text and lines on the page only use the accent when it reads
 *   on white; otherwise they use the ink colour, so yellow is never text or a line on a light surface.
 *
 * The result is a set of `--brand-*` CSS variables (apps/web/src/index.css maps them to the semantic tokens) and the
 * same values for the iOS app. The rules are the ones apps/web/src/lib/brandContrast.test.ts checks for every theme.
 * Two built-in palettes were tuned by hand before branding became data (Trade Outlet's black and #fff200; The Hub's
 * slate and indigo); for those exact colours the tuned values win, so Trade Outlet renders exactly as it always has.
 */
import {
  AA_TEXT, AA_UI, contrastHex, hueOf, mixHex, normalizeHex, parseHex, scaleHex, shiftLightness, toHsl,
} from './color';

export const INK_BLACK = '#0a0a0a';
const WHITE = '#ffffff';
const DARK_SURFACE = '#020617';
const CHROME_FG = '#e5e5e5';
const CHROME_MUTED = '#a3a3a3';
/** The rail must carry light text comfortably: at least this against white. */
const CHROME_MIN = 8;
/** How much lighter the active rail row is than the rail. */
const RAISED = 0.147;
/** Light panels accent text and lines must also read on: chat bubbles (slate-100) and grey panels (slate-200). */
const SLATE_100 = '#f1f5f9';
const SLATE_200 = '#e2e8f0';
/** Pale tints stay soft: at most this contrast against white. */
const TINT_MAX = 1.25;

export interface BrandColors { primary: string; accent: string }

/** The `--brand-*` variables, in index.css order. Light-mode values unless the name says dark. */
export const BRAND_VARS = [
  'accent', 'accent-hover', 'accent-pressed', 'on-accent', 'on-accent-strong', 'on-accent-muted', 'accent-tint', 'accent-tint-hover',
  'accent-text', 'accent-line', 'link-decoration', 'ink', 'chrome', 'chrome-soft', 'chrome-raised', 'chrome-fg',
  'chrome-muted', 'chrome-accent', 'on-chrome-accent', 'chrome-bar', 'chrome-active', 'dark-accent-tint', 'dark-reaction', 'dark-reaction-hover',
  'dark-accent-line',
] as const;
export type BrandVar = (typeof BRAND_VARS)[number];
export type BrandVars = Record<BrandVar, string>;

export interface BrandTheme {
  /** The colours actually used (after any adjustment). */
  primary: string;
  accent: string;
  vars: BrandVars;
  /** Plain-English notes on what was adjusted or might be a problem, for the Settings preview. */
  notes: string[];
  /** True when a colour had to change to stay readable. */
  adjusted: boolean;
}

/** Hand-tuned values for the built-in palettes (keyed `primary|accent`). */
const TUNED: Record<string, Partial<BrandVars>> = {
  // Trade Outlet, as index.css had it before t374u (docs/branding.md).
  '#0a0a0a|#fff200': {
    'accent-tint': '#fffbc2', 'accent-tint-hover': '#fff6a3', 'dark-accent-tint': '#2f2c00',
    'dark-reaction': '#34310a', 'dark-reaction-hover': '#46420f',
    // The phone tab bars were pure black with a deeper yellow for the active label.
    'chrome-bar': '#000000', 'chrome-active': '#f5c400',
  },
};

const best = (bg: string) => (contrastHex(INK_BLACK, bg) >= contrastHex(WHITE, bg) ? INK_BLACK : WHITE);
const rgbList = (hex: string) => parseHex(hex)!.join(' ');

export function deriveBrandTheme(colors: Partial<BrandColors> | null | undefined, fallback: BrandColors): BrandTheme {
  const notes: string[] = [];
  let adjusted = false;
  let primary = normalizeHex(colors?.primary) ?? normalizeHex(fallback.primary)!;
  let accent = normalizeHex(colors?.accent) ?? normalizeHex(fallback.accent)!;
  const pickedPrimary = primary, pickedAccent = accent;

  // Chrome (primary): dark enough for light text.
  // ...including on the hovered and active rows, which are a little lighter.
  const chromeOk = (c: string) => contrastHex(WHITE, c) >= CHROME_MIN && contrastHex(CHROME_FG, mixHex(c, WHITE, 1 - RAISED)) >= AA_TEXT;
  if (!chromeOk(primary)) {
    primary = shiftLightness(primary, 'darker', chromeOk);
    notes.push(`The primary colour was darkened to ${primary} so the navigation text stays readable.`);
    adjusted = true;
  }
  const chrome = primary;
  const chromeSoft = mixHex(chrome, WHITE, 1 - 0.0857);
  const chromeRaised = mixHex(chrome, WHITE, 1 - RAISED);
  let chromeMuted = CHROME_MUTED;
  if (contrastHex(chromeMuted, chromeRaised) < AA_TEXT) chromeMuted = shiftLightness(chromeMuted, 'lighter', c => contrastHex(c, chromeRaised) >= AA_TEXT);
  const ink = chrome;

  // Accent fill and the text on it.
  let onAccent = best(accent);
  if (contrastHex(onAccent, accent) < AA_TEXT) {
    const dir = onAccent === WHITE ? 'darker' : 'lighter';
    accent = shiftLightness(accent, dir, c => contrastHex(onAccent, c) >= AA_TEXT);
    notes.push(`The accent was ${dir === 'darker' ? 'darkened' : 'lightened'} to ${accent} so text on it is readable.`);
    adjusted = true;
    onAccent = best(accent);
  }
  const onAccentStrong = onAccent === INK_BLACK ? '#000000' : WHITE;
  // Muted text on the fill (descriptions on an active item): 75% of the full-strength text where that still reads.
  let mutedW = 0.75;
  while (mutedW < 1 && contrastHex(mixHex(onAccentStrong, accent, mutedW), accent) < AA_TEXT) mutedW = Math.min(1, mutedW + 0.05);
  const onAccentMuted = mixHex(onAccentStrong, accent, mutedW);
  const step = (f: number) => {
    const darker = scaleHex(accent, f);
    return contrastHex(onAccent, darker) >= AA_TEXT ? darker : mixHex(accent, WHITE, f);
  };

  // Pale tints (selection, your chat bubble, your reaction): lighter for strong accents that take white text, and
  // never so strong that they stop reading as a tint.
  const tint = (w0: number, max: number) => { let w = w0; while (w > 0.04 && contrastHex(mixHex(accent, WHITE, w), WHITE) > max) w -= 0.01; return mixHex(accent, WHITE, w); };
  const accentTint = tint(onAccent === WHITE ? 0.14 : 0.24, TINT_MAX);
  const accentTintHover = tint(onAccent === WHITE ? 0.22 : 0.36, TINT_MAX + 0.15);

  // Accent text and lines on light surfaces (the page, your chat bubble, grey panels): the accent only where it
  // reads on all of them, else ink.
  const textOk = [WHITE, SLATE_100, accentTint].every(bg => contrastHex(accent, bg) >= AA_TEXT);
  const lineOk = [WHITE, SLATE_200].every(bg => contrastHex(accent, bg) >= AA_UI);
  const accentText = textOk ? accent : ink;
  const accentLine = lineOk ? accent : ink;
  if (!lineOk) notes.push('The accent is too light for text or lines on white, so those use the primary colour; the accent stays a fill with dark text on it.');

  // On the dark rail: the indicator bar, focus ring and badges.
  const railOk = (c: string) => contrastHex(c, chrome) >= AA_UI && contrastHex(c, chromeRaised) >= AA_UI && contrastHex(best(c), c) >= AA_TEXT;
  const chromeAccent = railOk(accent) ? accent : shiftLightness(accent, 'lighter', railOk);
  if (chromeAccent !== accent) notes.push(`On the navigation rail the accent shows as ${chromeAccent} so it stands out.`);

  // Text in the accent on the rail and tab bars (the active tab label): it must read as text there.
  const chromeActive = contrastHex(chromeAccent, chrome) >= AA_TEXT ? chromeAccent : shiftLightness(chromeAccent, 'lighter', c => contrastHex(c, chrome) >= AA_TEXT);

  // Dark mode: lines and focus rings in the accent where they read on the dark page.
  const darkLine = contrastHex(accent, DARK_SURFACE) >= AA_UI ? accent : shiftLightness(accent, 'lighter', c => contrastHex(c, DARK_SURFACE) >= AA_UI);

  // Warnings that aren't blockers.
  const [, sat] = toHsl(parseHex(accent)!);
  const near = (hex: string) => { const d = Math.abs(hueOf(accent) - hueOf(hex)); return Math.min(d, 360 - d) < 15; };
  if (sat > 0.4 && (near('#c2410c') || near('#be123c'))) notes.push('The accent is close to the warning orange or error red, so warnings may stand out less.');
  if (contrastHex(accent, WHITE) < 1.05 && sat < 0.2) notes.push('The accent is almost white: buttons will be hard to see on white pages.');
  if (pickedPrimary === pickedAccent) notes.push('The primary and accent colours are the same, so active items and buttons will not stand out on the navigation rail.');

  const vars: BrandVars = {
    accent, 'accent-hover': step(0.92), 'accent-pressed': step(0.84), 'on-accent': onAccent, 'on-accent-strong': onAccentStrong, 'on-accent-muted': onAccentMuted,
    'accent-tint': accentTint, 'accent-tint-hover': accentTintHover,
    'accent-text': accentText, 'accent-line': accentLine, 'link-decoration': `rgb(${rgbList(accentText)} / 0.45)`,
    ink, chrome, 'chrome-soft': chromeSoft, 'chrome-raised': chromeRaised, 'chrome-fg': CHROME_FG, 'chrome-muted': chromeMuted,
    'chrome-accent': chromeAccent, 'on-chrome-accent': best(chromeAccent), 'chrome-bar': chrome, 'chrome-active': chromeActive,
    'dark-accent-tint': mixHex(accent, '#000000', 0.184), 'dark-reaction': mixHex(accent, INK_BLACK, 0.17),
    'dark-reaction-hover': mixHex(accent, '#0f0f0f', 0.225), 'dark-accent-line': darkLine,
    ...TUNED[`${pickedPrimary}|${pickedAccent}`],
  };
  return { primary, accent, vars, notes, adjusted };
}

/** CSS text for a theme: `--brand-*` custom properties on the given selector. */
export function brandThemeCss(theme: BrandTheme, selector = ':root:root') {
  return `${selector}{${BRAND_VARS.map(k => `--brand-${k}:${theme.vars[k]}`).join(';')}}`;
}
